import React, { useEffect, useMemo, useState } from 'react';
import { misc } from '../services/api';
import { ExternalLink, Newspaper, Pin, Award, CheckCircle2, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NewsQuizModal } from '../components/NewsQuizModal';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';
import { PBB_LOGO } from '../constants/brand';
import AppImage from '../components/AppImage';
import SiteSections from '../components/SiteSections';

const POLL_MS = 30 * 60 * 1000;
const fmtDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const ReadBadge = ({ read }) => read
  ? <span className={`inline-flex items-center gap-1 ${read.points ? 'text-green-400' : 'text-gray-500'}`} data-testid="news-read-badge"><CheckCircle2 size={11}/> {read.points ? `+${read.points} earned` : 'Answered'}</span>
  : <span className="inline-flex items-center gap-1 text-yellow-400" data-testid="news-earn-badge"><Award size={11}/> Read & earn</span>;

const ArticleCard = ({ a, pinned, read, showEarn, onOpen }) => (
  <a href={a.link} target="_blank" rel="noopener noreferrer" data-testid={`news-article-${a.id}`} onClick={() => onOpen?.(a)}
    className="pbb-card overflow-hidden flex group transition-colors hover:border-green-500/50">
    <div className="w-28 sm:w-44 shrink-0">
      <AppImage src={a.image} alt={a.title} aspect="1/1" rounded="none" fallbackSrc={PBB_LOGO} overlay={!!a.image} />
    </div>
    <div className="p-4 flex-1 min-w-0">
      <div className="text-xs text-green-400 flex items-center gap-1.5 flex-wrap">
        {pinned && <span className="inline-flex items-center gap-1 text-yellow-400"><Pin size={11}/> Pinned ·</span>}
        <span className="font-semibold" data-testid="news-article-source">{a.source}</span>
        {(a.publishedAt || a.time) && <span className="text-gray-500">· {a.publishedAt ? fmtDate(a.publishedAt) : a.time}</span>}
        {showEarn && <span className="ml-auto"><ReadBadge read={read}/></span>}
      </div>
      <div className="font-semibold mt-1 leading-snug group-hover:text-green-300 line-clamp-2" data-testid="news-article-title">{a.title}</div>
      {(a.summary || a.description || a.excerpt) && (
        <p className="text-xs text-gray-400 mt-1.5 line-clamp-3">{a.summary || a.description || a.excerpt}</p>
      )}
      <div className="text-xs text-gray-500 mt-2 inline-flex items-center gap-1">Read on {a.source} <ExternalLink size={12}/></div>
    </div>
  </a>
);

const SkeletonCard = ({ i }) => (
  <div className="pbb-card overflow-hidden flex animate-pulse" data-testid={`news-skeleton-${i}`} style={{ animationDelay: `${i * 120}ms` }}>
    <div className="w-28 sm:w-44 shrink-0 aspect-square bg-[#13261a]"/>
    <div className="p-4 flex-1 space-y-2.5">
      <div className="h-3 w-32 rounded bg-[#1a3322]"/>
      <div className="h-4 w-11/12 rounded bg-[#1f1f1f]"/>
      <div className="h-4 w-3/4 rounded bg-[#1f1f1f]"/>
      <div className="h-3 w-full rounded bg-[#181818]"/>
      <div className="h-3 w-2/3 rounded bg-[#181818]"/>
    </div>
  </div>
);

const StreakDots = ({ streak }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/5 text-xs text-orange-300" data-testid="news-streak">
    <Flame size={13}/> {streak.days}/{streak.target}-day streak
    <span className="inline-flex gap-0.5">
      {Array.from({ length: streak.target }).map((_, i) => (
        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < streak.days ? 'bg-orange-400' : 'bg-orange-400/20'}`}/>
      ))}
    </span>
    <span className="text-orange-400/80">+{streak.bonus} pts at {streak.target}</span>
  </div>
);

const StreakSmsToggle = ({ mine, setMine }) => {
  const flip = async (v) => {
    setMine(m => ({ ...m, streakSms: v }));
    try { await misc.setStreakSms(v); toast({ title: v ? 'Streak reminders on' : 'Streak reminders off' }); }
    catch { setMine(m => ({ ...m, streakSms: !v })); toast({ title: 'Could not update setting' }); }
  };
  return (
    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2a2a2a] text-xs text-gray-300" data-testid="streak-sms-toggle-wrap"
      title={mine.smsReady ? 'Text at 7pm ET if your streak is about to break' : 'Add a phone number and allow texts in your profile to receive reminders'}>
      <Switch checked={mine.streakSms} onCheckedChange={flip} data-testid="streak-sms-toggle"/>
      Streak reminders{!mine.smsReady && <span className="text-gray-500">(texts off in profile)</span>}
    </label>
  );
};

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-colors ${active ? 'bg-green-600 border-green-600 text-white' : 'border-[#2a2a2a] text-gray-300 hover:border-green-600/60'}`}>
    {children}
  </button>
);

export default function RSSFeed() {
  const [data, setData] = useState({ pinned: [], articles: [], sources: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { user, refresh } = useAuth();
  const [mine, setMine] = useState(null);
  const [quizFor, setQuizFor] = useState(null);

  useEffect(() => { if (user) misc.newsMyReads().then(setMine).catch(() => {}); }, [user]);
  const onOpen = user ? (a) => setQuizFor(a) : undefined;
  const onDone = (aid, r) => {
    setMine(m => ({ ...m, earnedToday: r.earnedToday, reads: { ...(m?.reads || {}), [aid]: { correct: r.correct, points: r.points } },
      streak: { ...m?.streak, days: r.streakBonus ? 0 : r.streakDays, readToday: true } }));
    if (r.points || r.streakBonus) refresh?.();
  };

  useEffect(() => {
    const load = () => misc.newsFeed().then(setData).finally(() => setLoading(false));
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const articles = useMemo(
    () => filter === 'all' ? data.articles : data.articles.filter(a => a.sourceId === filter),
    [filter, data.articles]);
  const empty = !data.pinned.length && !data.articles.length;

  const body = (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold">News Feed</h1>
        <p className="text-gray-400 text-sm">Fresh headlines from trusted Black media, updated every 30 minutes.</p>
        {user && mine && (
          <div className="mt-3 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/5 text-xs text-yellow-300" data-testid="news-points-progress">
            <Award size={13}/> Read an article, answer 1 question, earn {mine.pointsPerRead} pts · {mine.earnedToday}/{mine.cap} today
          </div>
          {mine.streak && <StreakDots streak={mine.streak}/>}
          <StreakSmsToggle mine={mine} setMine={setMine}/>
          </div>
        )}
      </div>
      {loading ? (
        <div className="space-y-4" data-testid="news-loading">
          <div className="flex gap-2">{[0, 1, 2, 3].map(i => <div key={i} className="h-7 w-24 rounded-full bg-[#1a1a1a] animate-pulse"/>)}</div>
          {[0, 1, 2, 3, 4].map(i => <SkeletonCard key={i} i={i}/>)}
        </div>
      ) : empty ? (
        <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="news-empty-state">
          <Newspaper className="mx-auto text-green-400" size={30} />
          <h3 className="mt-4 text-xl font-bold">Headlines are loading in</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">We're pulling the latest stories from our news partners. Check back in a few minutes.</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="news-source-chips">
            <Chip active={filter === 'all'} onClick={() => setFilter('all')} testId="news-chip-all">All sources</Chip>
            {data.sources.map(s => (
              <Chip key={s.id} active={filter === s.id} onClick={() => setFilter(s.id)} testId={`news-chip-${s.id}`}>{s.name}</Chip>
            ))}
          </div>
          <div className="space-y-4" data-testid="news-list">
            {filter === 'all' && data.pinned.map(a => <ArticleCard key={a.id} a={a} pinned />)}
            {articles.map(a => <ArticleCard key={a.id} a={a} showEarn={!!user} read={mine?.reads?.[a.id]} onOpen={onOpen} />)}
            {!articles.length && <div className="text-sm text-gray-500" data-testid="news-filter-empty">No recent articles from this source.</div>}
          </div>
        </>
      )}
      <NewsQuizModal article={quizFor} onClose={() => setQuizFor(null)} onDone={onDone} />
      <SiteSections pageKey="rss" />
    </div>
  );
  if (user) return body;
  return (
    <div className="bg-neutral-950 text-white min-h-screen py-10" data-testid="news-public-shell">
      <div className="max-w-3xl mx-auto px-4">{body}</div>
    </div>
  );
}
